/**
 * Heatmap — one camera's person-activity grid drawn over its live view, with every other
 * reporting camera one click away on the right.
 *
 * This used to be two pages. `/reports/heatmap` was a table of cameras and their coverage, and
 * clicking a row routed away to the heatmap itself; coming back meant the browser's Back button
 * and a full refetch of the table. That split cost the operator the only thing they were ever
 * doing here — comparing cameras. A heatmap is not a record you read once, it's a picture you
 * flick between, and a list whose entire purpose is "pick the next one" belongs BESIDE the
 * picture rather than one navigation away from it.
 *
 * So the list became a rail. It carries what the table's columns carried — hits, records, how
 * long ago each camera last reported — because those are what tell you which camera is worth
 * looking at next, and it stays on screen while you look. Cameras with no data are still
 * accounted for, in one line at the foot of the rail: "why is there no heatmap for the loading
 * bay" is answered by the model not being attached, and that answer must not vanish just
 * because the row it used to live on did.
 *
 * The live player is the one piece the two dashboards genuinely don't share (SFU on the local
 * node, a tunnelled player in the cloud), so it arrives as a render slot.
 */
import { useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { ArrowPathIcon, ChevronRightIcon, CursorArrowRaysIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useApi } from '../api';
import type { CameraRef, HeatmapAggregate, HeatmapCameraSummary } from '../api/contracts';
// Direct, not through the components barrel: this page only wants the colour ramp for its
// legend, and pulling the barrel in would drag every shared component into the chunk with it.
import { JET_GRADIENT } from '../components/common/HeatmapOverlay';
import { PageTitle } from '../components/layout/TitleBar';

/** The ranges the grid can be summed over, as the reference lays them out. */
const RANGES = [
  { value: '5m', label: '5 min', ms: 5 * 60e3 },
  { value: '15m', label: '15 min', ms: 15 * 60e3 },
  { value: '30m', label: '30 min', ms: 30 * 60e3 },
  { value: '1h', label: '1 hour', ms: 60 * 60e3 },
  { value: '6h', label: '6 hours', ms: 6 * 60 * 60e3 },
  { value: '24h', label: '24 hours', ms: 24 * 60 * 60e3 },
  { value: 'custom', label: 'Custom', ms: null },
] as const;

type RangeValue = (typeof RANGES)[number]['value'];

const DEFAULT_RANGE: RangeValue = '1h';
const REFRESH_MS = 30e3;

const fmtCount = (n: number) => Number(n || 0).toLocaleString();

/** "14s ago" / "6m ago" / "12 Jun" — how long ago is the point until it stops being. */
function fmtAge(iso: string | null, now: number): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return '—';
  const secs = Math.max(0, Math.round((now - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days}d ago`;
  return new Date(then).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

/** A sum of reporting intervals, not a wall-clock span. */
function fmtDuration(seconds: number): string {
  if (!seconds) return '—';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hours = mins / 60;
  return `${hours < 10 ? Math.round(hours * 10) / 10 : Math.round(hours)} h`;
}

/** Camera group name, wherever this app's camera list happens to keep it. */
function groupOf(camera: CameraRef): string | null {
  const c = camera as any;
  return c.group_name || c.groupName || c.group?.name || c.site_name || null;
}

/** One camera on the rail: identity from the camera list, coverage from the metrics summary. */
interface RailCamera {
  camera_id: string;
  name: string;
  group: string | null;
  /** Undefined when this app can't resolve model attachments — NOT the same as "not attached". */
  hasHeatmapModel: boolean | undefined;
  summary: HeatmapCameraSummary | null;
}

/** Everything the host app needs to build its own player, plus the layer to stack on it. */
export interface HeatmapPlayerArgs {
  cameraId: string;
  cameraName: string;
  /**
   * Props for the shared `HeatmapOverlay`, or null when there is nothing to draw (no data, or
   * the operator switched the layer off). Handed over rather than rendered here so the app
   * decides where in its own player the layer is stacked.
   */
  overlay: {
    spatialGrid: number[][];
    gridRows: number;
    gridCols: number;
    opacity: number;
    showGrid: boolean;
  } | null;
}

export interface HeatmapPageProps {
  /** Changes when the operator switches node (cloud); forces a refetch. */
  nodeKey?: string;
  /** Opens on this camera when set — the live view links straight to one. */
  initialCameraId?: string;
  /** Told whenever the operator picks a different camera, so the host can update the URL. */
  onCameraChange?: (cameraId: string, cameraName: string) => void;
  /** The app's live player, with the heat layer stacked on it. */
  renderPlayer: (args: HeatmapPlayerArgs) => ReactNode;
  /** Rendered instead of the page when the host has nothing to query (cloud: no node). */
  emptyState?: ReactNode;
}

export default function HeatmapPage({
  nodeKey = '',
  initialCameraId,
  onCameraChange,
  renderPlayer,
  emptyState,
}: HeatmapPageProps) {
  const { heatmap } = useApi();

  const [range, setRange] = useState<RangeValue>(DEFAULT_RANGE);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [summaries, setSummaries] = useState<HeatmapCameraSummary[]>([]);
  const [cameras, setCameras] = useState<CameraRef[]>([]);
  const [modelCameras, setModelCameras] = useState<Set<string> | null>(null);
  const [grid, setGrid] = useState<HeatmapAggregate | null>(null);

  const [selectedId, setSelectedId] = useState<string>(initialCameraId || '');
  /**
   * Two clocks, because they answer to different things: the rail reloads when the RANGE
   * changes, the grid also when the CAMERA does. Sharing one flag meant flicking down the rail
   * re-pulled every camera's coverage to redraw one picture.
   */
  const [railLoading, setRailLoading] = useState(true);
  const [gridLoading, setGridLoading] = useState(true);
  const loading = railLoading || gridLoading;
  const [error, setError] = useState<string | null>(null);
  /** Stamped per fetch, so "14s ago" is relative to the data rather than to each render. */
  const [fetchedAt, setFetchedAt] = useState(() => Date.now());

  const [showHeatmap, setShowHeatmap] = useState(true);
  const [showGrid, setShowGrid] = useState(false);
  const [opacity, setOpacity] = useState(0.6);

  /** The window the rail and the grid are both summed over. */
  const bounds = useCallback(() => {
    if (range === 'custom' && customFrom && customTo) {
      return { from: new Date(customFrom).getTime(), to: new Date(customTo).getTime() };
    }
    const ms = RANGES.find((r) => r.value === range)?.ms ?? RANGES[0].ms!;
    const to = Date.now();
    return { from: to - (ms ?? 5 * 60e3), to };
  }, [range, customFrom, customTo]);

  /**
   * The rail: coverage for every camera, the camera list, and which cameras the model is
   * attached to. Only the coverage decides success — losing the camera list costs a name,
   * losing the summary costs the page.
   */
  const fetchRail = useCallback(async () => {
    setError(null);
    setRailLoading(true);
    try {
      const { from, to } = bounds();
      const [summaryResult, cameraResult, modelResult] = await Promise.allSettled([
        heatmap.cameraSummary({ from, to }),
        heatmap.listCameras(),
        heatmap.heatmapCameraIds ? heatmap.heatmapCameraIds() : Promise.resolve(null),
      ]);
      if (summaryResult.status === 'rejected') throw summaryResult.reason;
      setSummaries(Array.isArray(summaryResult.value) ? summaryResult.value : []);
      setCameras(cameraResult.status === 'fulfilled' && Array.isArray(cameraResult.value) ? cameraResult.value : []);
      setModelCameras(
        modelResult.status === 'fulfilled' && Array.isArray(modelResult.value) ? new Set(modelResult.value) : null,
      );
      setFetchedAt(Date.now());
    } catch (e: any) {
      console.error('heatmap rail fetch failed', e);
      // A 404 means the node's command-center predates /heatmap/cameras, which is a different
      // problem from an empty result — say so instead of showing an empty rail.
      setError(
        e?.response?.status === 404
          ? 'The command-center service does not have the heatmap report endpoint — it needs an updated build.'
          : 'Could not load heatmap coverage.',
      );
      setSummaries([]);
      setCameras([]);
      setModelCameras(null);
    } finally {
      setRailLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatmap, bounds, nodeKey]);

  const rows = useMemo<RailCamera[]>(() => {
    const byCamera = new Map(summaries.map((s) => [s.camera_id, s]));
    const known = cameras.map((c) => {
      const id = String(c.id ?? (c as any).camera_id ?? '');
      return {
        camera_id: id,
        name: c.name || id || 'Camera',
        group: groupOf(c),
        hasHeatmapModel: modelCameras ? modelCameras.has(id) : undefined,
        summary: byCamera.get(id) || null,
      };
    });
    // A camera deleted since it reported still has metrics rows; dropping it would silently
    // lose that history.
    const seen = new Set(known.map((r) => r.camera_id));
    const orphaned = summaries
      .filter((s) => !seen.has(s.camera_id))
      .map((s) => ({
        camera_id: s.camera_id,
        name: `Unknown camera (${s.camera_id.slice(0, 8)})`,
        group: null,
        hasHeatmapModel: undefined,
        summary: s,
      }));
    return [...known, ...orphaned];
  }, [cameras, summaries, modelCameras]);

  /** Busiest first — the rail is a ranking, and the top of it is where to look. */
  const reporting = useMemo(
    () => rows.filter((r) => r.summary).sort((a, b) => (b.summary!.total_hits ?? 0) - (a.summary!.total_hits ?? 0)),
    [rows],
  );
  const silent = rows.length - reporting.length;
  const notAttached = rows.filter((r) => !r.summary && r.hasHeatmapModel === false).length;

  const selected = useMemo(
    () => rows.find((r) => r.camera_id === selectedId) || null,
    [rows, selectedId],
  );

  /**
   * Land on the busiest reporting camera when the page opens without one.
   *
   * Only while nothing is selected — re-running it on every rail refresh would drag the
   * operator off the camera they picked the moment another one got busier.
   */
  useEffect(() => {
    // A selected camera the rail doesn't contain is not a selection, it is a leftover: an id
    // from the site the operator has just switched away from, or a camera since deleted. It
    // must be dropped rather than kept, or the grid keeps asking for a camera this node has
    // never heard of and the auto-pick below can never run — an empty picture beside a rail
    // with nothing highlighted. Held until the rail has actually loaded, so an id that IS
    // valid isn't discarded while its list is still in flight.
    if (selectedId && !railLoading && rows.length > 0 && !rows.some((r) => r.camera_id === selectedId)) {
      setSelectedId('');
      return;
    }
    if (selectedId || reporting.length === 0) return;
    setSelectedId(reporting[0].camera_id);
    // Told to the host as well, so the URL names the camera being shown from the first paint —
    // an operator who pastes the link should get the view they were looking at, not "whichever
    // camera happens to be busiest when you open this".
    onCameraChange?.(reporting[0].camera_id, reporting[0].name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId, reporting, rows, railLoading]);

  // The rail's widest bar is the busiest camera, so every other bar reads as a share of it.
  const peakHits = reporting[0]?.summary?.total_hits || 0;

  /** The selected camera's own grid. Separate from the rail: it changes when the pick does. */
  const fetchGrid = useCallback(async () => {
    if (!selectedId) { setGrid(null); setGridLoading(false); return; }
    setGridLoading(true);
    try {
      const { from, to } = bounds();
      setGrid((await heatmap.aggregated(selectedId, from, to)) || null);
    } catch (e) {
      console.error('heatmap grid fetch failed', e);
      setGrid(null);
    } finally {
      setGridLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heatmap, selectedId, bounds, nodeKey]);

  /** Both, for the refresh button and the auto-refresh tick. */
  const refresh = useCallback(async () => {
    await Promise.all([fetchRail(), fetchGrid()]);
  }, [fetchRail, fetchGrid]);

  // Keep the timer off the identity of `refresh`, which changes on every range/camera edit —
  // rebuilding the interval each time meant a fast series of clicks could fire several.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;

  // Separately, so picking a camera redraws the picture without re-pulling the rail.
  useEffect(() => { fetchRail(); }, [fetchRail]);
  useEffect(() => { fetchGrid(); }, [fetchGrid]);

  useEffect(() => {
    if (!autoRefresh || range === 'custom') return;
    const timer = setInterval(() => refreshRef.current(), REFRESH_MS);
    return () => clearInterval(timer);
  }, [autoRefresh, range]);

  const pick = (row: RailCamera) => {
    if (row.camera_id === selectedId) return;
    setSelectedId(row.camera_id);
    onCameraChange?.(row.camera_id, row.name);
  };

  const spatialGrid = grid?.spatial_grid;
  const hasGrid = Array.isArray(spatialGrid) && spatialGrid.length > 0;
  const rangeLabel = RANGES.find((r) => r.value === range)?.label.toLowerCase() ?? '';

  /** Where the busiest cell sits, so the picture's hottest spot has a name to quote. */
  const peakCell = useMemo(() => {
    if (!hasGrid) return null;
    let best = -1;
    let at: [number, number] = [0, 0];
    spatialGrid!.forEach((row, r) => row.forEach((v, c) => { if (v > best) { best = v; at = [r, c]; } }));
    return best > 0 ? `R${at[0]}·C${at[1]}` : null;
  }, [hasGrid, spatialGrid]);

  if (emptyState) return <>{emptyState}</>;

  return (
    <div className="space-y-3">
      <PageTitle
        title={`Heatmap${selected ? ` — ${selected.name}` : ''}`}
        subtitle="Person activity heatmap drawn over the live camera stream."
      >
        <span className="flex items-center gap-1.5 text-[12px] text-muted-foreground">
          <span className={`h-2 w-2 rounded-full ${autoRefresh && range !== 'custom' ? 'bg-success' : 'bg-muted-foreground'}`} />
          {autoRefresh && range !== 'custom' ? 'Live' : 'Paused'} · updated {fmtAge(new Date(fetchedAt).toISOString(), Date.now())}
        </span>
        <button
          onClick={refresh}
          disabled={loading}
          aria-label="Refresh"
          title="Refresh"
          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </PageTitle>

      {/* ── Time range ── */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-card px-3 py-2 shadow-card">
        <span className="text-[11px] font-bold uppercase tracking-[0.08em] text-muted-foreground">Time range</span>
        <div className="flex flex-wrap gap-2">
          {RANGES.map((r) => (
            <button
              key={r.value}
              onClick={() => setRange(r.value)}
              className={`rounded-lg border px-3 py-1.5 text-[13px] font-medium transition-colors ${
                range === r.value
                  ? 'border-brand bg-brand text-white'
                  : 'border-border text-foreground hover:bg-muted'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>

        {range === 'custom' && (
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="datetime-local"
              value={customFrom}
              onChange={(e) => setCustomFrom(e.target.value)}
              aria-label="From"
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-[12px] text-foreground"
            />
            <span className="text-[12px] text-muted-foreground">to</span>
            <input
              type="datetime-local"
              value={customTo}
              onChange={(e) => setCustomTo(e.target.value)}
              aria-label="To"
              className="rounded-lg border border-border bg-background px-3 py-1.5 text-[12px] text-foreground"
            />
            <button
              onClick={refresh}
              className="rounded-lg bg-brand px-3 py-1.5 text-[12px] font-medium text-white transition-colors hover:bg-brand/90"
            >
              Apply
            </button>
          </div>
        )}

        <span className="flex-1" />

        <label className="flex cursor-pointer items-center gap-2 text-[13px] text-muted-foreground">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            disabled={range === 'custom'}
            className="h-4 w-4 accent-brand"
          />
          Auto refresh · 30s
        </label>
      </div>

      {error && (
        <div className="rounded-xl border border-border bg-red-50 p-4 text-[13px] text-destructive dark:bg-red-900/20">
          {error}
        </div>
      )}

      {/* ── The picture, and the rail of cameras to compare it against ── */}
      <div className="grid gap-3 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="min-w-0 space-y-3">
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
            <div className="flex flex-wrap items-center gap-4 border-b border-border px-4 py-3">
              <h2 className="text-[14px] font-semibold text-foreground">Live view</h2>
              <span className="flex-1" />
              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-foreground">
                <input type="checkbox" checked={showHeatmap} onChange={(e) => setShowHeatmap(e.target.checked)} className="h-4 w-4 accent-brand" />
                Heatmap
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-[13px] text-foreground">
                <input type="checkbox" checked={showGrid} onChange={(e) => setShowGrid(e.target.checked)} className="h-4 w-4 accent-brand" />
                Grid
              </label>
              <label className="flex items-center gap-2 text-[13px] text-muted-foreground">
                Opacity
                <input
                  type="range" min="0.1" max="1" step="0.05" value={opacity}
                  onChange={(e) => setOpacity(parseFloat(e.target.value))}
                  className="w-24 accent-brand"
                />
                <span className="w-10 text-right tabular-nums font-medium text-foreground">{Math.round(opacity * 100)}%</span>
              </label>
            </div>

            <div className="relative aspect-video w-full bg-black">
              {selected ? (
                renderPlayer({
                  cameraId: selected.camera_id,
                  cameraName: selected.name,
                  overlay: showHeatmap && hasGrid
                    ? {
                      spatialGrid: spatialGrid!,
                      gridRows: grid!.grid_rows,
                      gridCols: grid!.grid_cols,
                      opacity,
                      showGrid,
                    }
                    : null,
                })
              ) : (
                <div className="flex h-full items-center justify-center text-[13px] text-white/60">
                  {loading ? 'Loading…' : 'No camera is reporting a heatmap in this range.'}
                </div>
              )}

              {selected && !hasGrid && !loading && (
                <div className="absolute left-3 top-3 rounded-lg bg-black/70 px-3 py-1 text-[11px] text-white/80" style={{ zIndex: 7 }}>
                  No heatmap data for the selected time range
                </div>
              )}

              {/* The scale, on the picture. A colour ramp read four inches away from the thing
                  it explains is a legend nobody consults. */}
              {hasGrid && showHeatmap && (
                <div
                  className="pointer-events-none absolute bottom-3 right-3 flex items-center gap-2.5 rounded-lg bg-black/75 px-3 py-1.5 backdrop-blur-sm"
                  style={{ zIndex: 7 }}
                >
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">Idle</span>
                  <span className="h-2 w-28 rounded-full" style={{ background: `linear-gradient(to right, ${JET_GRADIENT})` }} />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-white/70">Busiest</span>
                  {peakCell && <span className="text-[12px] font-bold text-white">peak {peakCell}</span>}
                </div>
              )}
            </div>
          </div>

          {/* ── What the selected camera reported over the range ── */}
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Tile
              label="Total hits"
              value={fmtCount(grid?.total_hits ?? 0)}
              hint="activity samples, not people"
              tone="info"
            />
            <Tile label="Records" value={fmtCount(grid?.event_count ?? 0)} hint="one per reporting interval" />
            <Tile
              label="Covered"
              value={fmtDuration(grid?.total_interval_seconds ?? 0)}
              hint={`of the selected ${rangeLabel}`}
            />
            <Tile
              label="Grid"
              value={grid?.grid_rows && grid?.grid_cols ? `${grid.grid_rows}×${grid.grid_cols}` : '—'}
              hint="cells the module accumulates into"
            />
          </div>

          {/* The caveat sits with the number it applies to, not behind a hover: "hits" is the
              figure everyone misreads as a headcount, and misreading it produces a confidently
              wrong operational conclusion. */}
          <div className="flex gap-2.5 rounded-xl border border-info/30 bg-info/5 px-4 py-3 text-[12px] text-muted-foreground">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-info" />
            <p>
              Hits are activity samples, not a count of people — roughly one per person per
              processed frame, so dwell time inflates them. The overlay normalises against this
              camera&apos;s own busiest cell, so colour shows <span className="font-medium text-foreground">where</span>{' '}
              activity was, never how much.
            </p>
          </div>
        </div>

        {/* ── Reporting cameras ── */}
        <aside className="min-w-0">
          <div className="overflow-hidden rounded-xl border border-border bg-card shadow-card">
            {/* The rail's header has to do one job the old one didn't: say that this is a
                PICKER. Operators were reading it as a summary table beside the heatmap and
                never discovering that the heatmap above changes when you click a row — so the
                title is now foreground weight and carries the instruction outright, with the
                counts demoted to the line under it. */}
            <div className="border-b border-border bg-muted/30 px-4 py-3">
              <h2 className="text-[13px] font-bold text-foreground">Reporting cameras</h2>
              <p className="mt-0.5 flex items-center gap-1.5 text-[12px] font-medium text-brand">
                <CursorArrowRaysIcon className="h-3.5 w-3.5 shrink-0" />
                Select a camera to show its heatmap
              </p>
              <p className="mt-0.5 text-[12px] text-muted-foreground">
                {reporting.length} of {rows.length} camera{rows.length === 1 ? '' : 's'} reporting · last {rangeLabel}
              </p>
            </div>

            <div className="max-h-[60vh] overflow-y-auto">
              {reporting.length === 0 ? (
                <p className="px-4 py-6 text-[12px] text-muted-foreground">
                  {loading ? 'Loading coverage…' : `No camera reported a heatmap in the ${rangeLabel}.`}
                </p>
              ) : (
                <ul>
                  {reporting.map((r) => {
                    const s = r.summary!;
                    const active = r.camera_id === selectedId;
                    const share = peakHits > 0 ? Math.max(2, Math.round((s.total_hits / peakHits) * 100)) : 0;
                    return (
                      <li key={r.camera_id}>
                        <button
                          onClick={() => pick(r)}
                          aria-current={active ? 'true' : undefined}
                          title={active ? `${r.name} — shown above` : `Show ${r.name}'s heatmap`}
                          className={`group relative w-full cursor-pointer border-l-[3px] border-b border-b-border px-4 py-3 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand ${
                            active
                              ? 'border-l-brand bg-brand/10'
                              : 'border-l-transparent hover:border-l-brand/40 hover:bg-brand/5'
                          }`}
                        >
                          <span className="flex items-baseline gap-2">
                            <span className={`min-w-0 flex-1 truncate text-[14px] font-semibold ${active ? 'text-brand' : 'text-foreground group-hover:text-brand'}`}>
                              {r.name}
                            </span>
                            <span className={`shrink-0 tabular-nums text-[14px] font-semibold ${active ? 'text-brand' : 'text-foreground'}`}>
                              {fmtCount(s.total_hits)}
                            </span>
                            {/* Which row you are looking at, and — on every other row — a
                                chevron saying the row goes somewhere. The chevron is faint at
                                rest and brand-coloured under the cursor: visible enough to be
                                found without scanning, quiet enough not to fight the ranking. */}
                            {active ? (
                              <span className="shrink-0 self-center rounded-full bg-brand px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                                Viewing
                              </span>
                            ) : (
                              <ChevronRightIcon className="h-4 w-4 shrink-0 self-center text-muted-foreground/50 transition-colors group-hover:text-brand" />
                            )}
                          </span>
                          {/* Share of the busiest camera. The rail is a ranking, and a bar is
                              what makes "70,194 against 3,636" legible without arithmetic. */}
                          <span className="mt-1.5 block h-1.5 overflow-hidden rounded-full bg-muted">
                            <span
                              className={`block h-full rounded-full ${active ? 'bg-brand' : 'bg-muted-foreground/50'}`}
                              style={{ width: `${share}%` }}
                            />
                          </span>
                          <span className="mt-1.5 block text-[12px] text-muted-foreground">
                            {fmtCount(s.entries)} records · {fmtAge(s.last_event_at, fetchedAt)}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>

            {/* The cameras that aren't here, and why. This is the answer the old table gave by
                listing every camera including the empty ones — kept, in one line. */}
            {silent > 0 && (
              <p className="border-t border-border px-4 py-3 text-[12px] text-muted-foreground">
                {notAttached > 0
                  ? `${notAttached} more camera${notAttached === 1 ? ' has' : 's have'} no heatmap model attached.`
                  : `${silent} more camera${silent === 1 ? '' : 's'} reported nothing in the ${rangeLabel}.`}
                {notAttached > 0 && silent > notAttached && (
                  <> {silent - notAttached} other{silent - notAttached === 1 ? '' : 's'} reported nothing in the {rangeLabel}.</>
                )}
              </p>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

/** One figure about the selected camera. Local to this page — four of them, one shape. */
function Tile({ label, value, hint, tone }: { label: string; value: string; hint: string; tone?: 'info' }) {
  return (
    <div className="rounded-xl border border-border bg-card px-4 py-3 shadow-card">
      <div className="text-[10px] font-bold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
      <div className={`mt-1 text-[24px] font-bold leading-tight tabular-nums ${tone === 'info' ? 'text-info' : 'text-foreground'}`}>
        {value}
      </div>
      <div className="mt-0.5 truncate text-[12px] text-muted-foreground" title={hint}>{hint}</div>
    </div>
  );
}
